/**
 * SEARCH TYPES - Tipos para el SearchEngine
 * 
 * Tipos compartidos para la capa de infraestructura de búsqueda.
 */

// ============================================
// DOCUMENTO BUSCABLE
// ============================================

/**
 * Interface base para documentos que pueden ser buscados
 * Los servicios de dominio deben mapear sus entidades a este formato
 */
export interface SearchableDocument {
    id: string;

    // Campos principales para búsqueda
    title?: string;
    content?: string;

    // Campos de indexación
    keywords?: string[];
    tags?: string[];
    conceptIds?: string[];

    // Embedding vectorial (para búsqueda semántica)
    embedding?: number[];
    embeddingModel?: string;

    // Metadata genérica
    [key: string]: unknown;
}

// ============================================
// OPCIONES DE BÚSQUEDA
// ============================================

export interface SearchOptions {
    /** Número máximo de resultados */
    maxResults?: number;

    /** Score mínimo para incluir en resultados */
    minScore?: number;

    /** Filtros de búsqueda */
    filters?: SearchFilters;

    /** Usar expansión semántica (default: true) */
    useSemanticExpansion?: boolean;

    /** Usar búsqueda vectorial (default: false, requiere embeddings) */
    useVectorSearch?: boolean;

    /** Pesos personalizados para scoring */
    weights?: ScoringWeights;
}

export interface SearchFilters {
    /** ID del propietario (therapistId, userId, schoolId, etc.) */
    ownerId?: string;

    /** Filtro por tags */
    tags?: string[];

    /** Filtro por conceptIds del Semantic Layer */
    conceptIds?: string[];

    /** Metadata adicional para filtrar */
    metadata?: Record<string, unknown>;
}

// ============================================
// RESULTADOS
// ============================================

export interface SearchResult<T = SearchableDocument> {
    /** Documento encontrado */
    document: T;

    /** Score de relevancia */
    score: number;

    /** Términos que coincidieron */
    matchedTerms: string[];

    /** Fuente del match */
    source: 'keyword' | 'vector' | 'hybrid';
}

// ============================================
// SCORING
// ============================================

export interface ScoringWeights {
    /** Peso por match en título */
    title: number;

    /** Peso por match en contenido */
    content: number;

    /** Peso por match en keywords */
    keywords: number;

    /** Peso por match en tags */
    tags: number;

    /** Peso por match en conceptIds del Semantic Layer */
    conceptMatch: number;

    /** Peso relativo de búsqueda por keywords vs vectorial */
    keywordSearchWeight: number;
    vectorSearchWeight: number;
}

export const DEFAULT_SCORING_WEIGHTS: ScoringWeights = {
    title: 5,
    content: 2,
    keywords: 3,
    tags: 2,
    conceptMatch: 5,
    keywordSearchWeight: 1.0,
    vectorSearchWeight: 1.5,
};

// ============================================
// EMBEDDINGS
// ============================================

export interface EmbeddingRequest {
    text: string;
    model?: string;
}

export interface EmbeddingResult {
    embedding: number[];
    model: string;
    dimensions: number;
}

export const EMBEDDING_MODELS = {
    VERTEX_AI: 'text-embedding-004',
    DIMENSIONS: 768,
} as const;
